import React from 'react'
import classnames from 'classnames'
import isArray from 'lodash/isArray'
import isPlainObject from 'lodash/isPlainObject'
import identity from 'lodash/identity'
import noop from 'lodash/noop'
import mergeWith from 'lodash/mergeWith'
import toPath from 'lodash/toPath'
import isEqual from 'lodash/isEqual'

import withContainer from '../components/hoc/withContainer'
import withHooks from '../components/hoc/withHooks'

/**
 * Recursively delete all properties matching with the given predicate function in the given value
 * @param {Object} value
 * @param {Function} predicate
 * @return the number of deleted properties or indexes
 */
export function deepDeleteAll(value, predicate) {
  let count = 0
  if (isArray(value)) {
    value.forEach((item, index) => {
      if (predicate(item)) {
        value.splice(index, 1)
        count++
      } else {
        count += deepDeleteAll(item, predicate)
      }
    })
  } else if (isPlainObject(value)) {
    Object.keys(value).forEach(key => {
      if (predicate(value[key])) {
        delete value[key]
        count++
      } else {
        count += deepDeleteAll(value[key], predicate)
      }
    })
  }
  return count
}

/**
 * Sort an array according to locale
 * @param {Array} array - the array to sort
 * @param {Function} getItem - accessor to the value to compare in each item
 */
export function sort(array = [], getItem = identity) {
  return array.sort(compareWith(getItem))
}

/**
 * Compare two values, returns 1 or -1 depending on which one comes first
 * @param {Function} getItem - accessor to the value to compare
 * @param {*} a
 * @param {*} b
 */
export const compareWith = (getItem = identity) => (a, b) => {
  const itemA = getItem(a)
  const itemB = getItem(b)
  if (itemA && itemA.localeCompare) return itemA.localeCompare(itemB)
  else return itemA > itemB ? 1 : -1
}

/**
 * Utility function to normalize a string (lowercase + removes unwanted spaces)
 *
 * @param {String} str - the string to normalize
 * @return {String} the normalized string
 */
export const normalize = str =>
  str
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .join(' ')

/**
 * Check if all the normalized words of a filter are contained in another normalized sentence
 * @param {String} filter
 * @param {String} sentence
 * @return {Boolean}
 */
export const isFilterInSentence = (filter, sentence) => {
  const normalizedSentence = normalize(sentence)
  return normalize(filter)
    .split(' ')
    .every(word => normalizedSentence.includes(word))
}

/**
 * Check if a path is the child of another
 *
 * @param {String} child
 * @param {String} path
 * @return {Boolean}
 */
export function isChildPath(child, parent) {
  child = toPath(child)
  parent = toPath(parent)
  if (child.length < parent.length) {
    return false
  } else {
    return parent.every((sub, index) => child[index] === sub)
  }
}

/**
 * Default strategies for merging props key by key
 */
export const defaultMergePropsStrategies = {
  style: (a, b) => ({ ...a, ...b }),
  className: classnames,
}

/**
 * Reduce an array of props object into a single object
 * @param {Object} strategies
 * @param  {...Object} propsObjects
 */
export const mergeProps = strategies => (...propsObjects) => {
  strategies = { ...defaultMergePropsStrategies, ...strategies }
  return mergeWith({}, ...propsObjects, (prop1, prop2, key, obj1, obj2) =>
    strategies.hasOwnProperty(key) ? strategies[key](prop1, prop2) : obj2.hasOwnProperty(key) ? prop2 : prop1
  )
}

/**
 * @typedef {{
 *    Provider: React.ExoticComponent,
 *    use: Function,
 *    withConsumer: Function,
 *    withProvider: Function
 * }} CustomContext
 */

/**
 * Create a context with Provider initialized with a HOC and with the Consumer being accessible directly as a hook with the provided method `use`
 * @param {Function} enhanceProvider - the HOC initializing the Provider's value
 *
 * @return CustomContext
 */
export function createContext(enhanceProvider = identity, defaultValue) {
  // create the context
  const Context = React.createContext(defaultValue)
  // enhance the Provider component
  const Provider = enhanceProvider(Context.Provider)
  // create the consumer hook
  function useContext() {
    return React.useContext(Context)
  }

  return {
    Provider,
    use: useContext,
    // create the provider HOC
    withProvider: (propsMapper = noop) => withContainer(Provider, props => propsMapper(props)),
    // create the consumer HOC
    withConsumer: (propsMapper = identity) => withHooks(props => propsMapper(React.useContext(Context), props)),
  }
}

/**
 * Check if a value or a nested value of an object verify the given predicate
 */
export function checkObject(object, predicate) {
  if (isPlainObject(object)) {
    return Object.keys(object).some(key => checkObject(object[key], predicate))
  } else {
    return predicate(object)
  }
}

/**
 * Debounce a function call by finally executing it when no new call with the same deps has been made
 *
 * @param {*} deps - The dependencies to check equality from
 * @param {Number} timeout - The maximum time to wait with no call before finally executing the last function call
 * @param {Function} callback - the function to be executed
 */
export function debounceUntilStable(deps, timeout, callback) {
  // get stored timeout
  const found = Object.entries(debounceUntilStable).find(([, value]) => isEqual(value, deps))

  // clear stored timeout
  if (found) {
    clearTimeout(found[0])
    delete debounceUntilStable[found[0]]
  }

  // create new timeout
  const id = setTimeout(() => {
    // delete stored timeout
    delete debounceUntilStable[id]
    // execute function
    callback(deps)
  }, timeout)

  // store it
  debounceUntilStable[id] = deps
}
